import React, { useState } from 'react'
import {
    Box,
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
} from '@material-ui/core'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { Bid, BidStatus, RFP } from '../../models'

interface PricingLine {
    kind: 'unit' | 'area'
    id: number
    name: string
    current: string | null
    bid: string | null
}

interface PricingPreview {
    in_my_vendors: boolean
    vendor_name: string
    service_name: string
    service_id: number | null
    lines: PricingLine[]
}

interface Props {
    bid: Bid
    rfp: RFP
    onBidUpdated: (bid: Bid) => void
}

const money = (value: string | null) => {
    if (value == null || value === '') {
        return '—'
    }
    const number = Number(value)
    if (!Number.isFinite(number)) {
        return '—'
    }
    return `$${number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`
}

const hasSubmittedBid = (bid: Bid) =>
    bid.status === BidStatus.SUBMITTED || bid.status === BidStatus.REVIEWED

const ChoiceCard = ({
    title,
    body,
    disabled,
    onClick,
}: {
    title: string
    body: string
    disabled?: boolean
    onClick: () => void
}) => {
    const theme = useTheme()
    return (
        <ButtonBase
            disabled={disabled}
            onClick={onClick}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '14px 16px',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 8,
                background: theme.palette.common.white,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
            }}
            onMouseEnter={(event) => {
                if (!disabled) {
                    event.currentTarget.style.borderColor =
                        theme.palette.primary.main
                    event.currentTarget.style.background = `${theme.palette.primary.main}0F`
                }
            }}
            onMouseLeave={(event) => {
                event.currentTarget.style.borderColor = theme.palette.grey[300]
                event.currentTarget.style.background =
                    theme.palette.common.white
            }}
        >
            <Typography style={{ fontWeight: 700, fontSize: 15 }}>
                {title}
            </Typography>
            <Typography
                color="textSecondary"
                style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}
            >
                {body}
            </Typography>
        </ButtonBase>
    )
}

const PricingLines = ({
    lines,
    showCurrent,
}: {
    lines: PricingLine[]
    showCurrent: boolean
}) => {
    const units = lines.filter((line) => line.kind === 'unit')
    const areas = lines.filter((line) => line.kind === 'area')
    const section = (title: string, rows: PricingLine[]) => {
        if (rows.length === 0) {
            return null
        }
        return (
            <Box style={{ marginTop: 12 }}>
                <Typography
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                        color: '#666',
                        marginBottom: 6,
                    }}
                >
                    {title}
                </Typography>
                {rows.map((row) => {
                    const changed =
                        showCurrent && (row.current || '') !== (row.bid || '')
                    return (
                        <Box
                            key={`${row.kind}-${row.id}`}
                            display="flex"
                            justifyContent="space-between"
                            style={{
                                fontSize: 13,
                                padding: '3px 0',
                                fontWeight: changed ? 700 : 400,
                            }}
                        >
                            <span>{row.name}</span>
                            <span>
                                {showCurrent
                                    ? `${money(row.current)} → ${money(
                                          row.bid,
                                      )}`
                                    : money(row.bid)}
                            </span>
                        </Box>
                    )
                })}
            </Box>
        )
    }
    return (
        <>
            {section('Areas', areas)}
            {section('Unit types', units)}
        </>
    )
}

export const RFPVendorContractActions = (props: Props) => {
    const { bid, rfp, onBidUpdated } = props
    const [open, setOpen] = useState(false)
    const [preview, setPreview] = useState<PricingPreview | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    if (!rfp.is_generated || !hasSubmittedBid(bid)) {
        return null
    }

    const inMyVendors = Boolean(bid.in_my_vendors)
    const label = inMyVendors ? 'Update pricing' : 'Add to My Vendors'

    const openDialog = async (event?: React.MouseEvent) => {
        event?.stopPropagation()
        setOpen(true)
        setLoading(true)
        try {
            const res = await axiosInstance.get<PricingPreview>(
                `service-contract/bid/${bid.id}/contract-pricing-preview/`,
            )
            setPreview(res.data)
        } catch (err: any) {
            toast.error(
                err?.response?.data?.error || 'Unable to load bid pricing.',
            )
            setOpen(false)
        } finally {
            setLoading(false)
        }
    }

    const close = (event?: React.MouseEvent) => {
        event?.stopPropagation()
        if (!saving) {
            setOpen(false)
        }
    }

    const run = async (
        path: string,
        body?: Record<string, boolean>,
        success?: string,
    ) => {
        setSaving(true)
        try {
            const res = await axiosInstance.post<Bid>(
                `service-contract/bid/${bid.id}/${path}/`,
                body,
            )
            onBidUpdated({ ...bid, ...res.data, in_my_vendors: true })
            toast.success(success || 'Saved.')
            setOpen(false)
        } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Unable to save.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <Button
                size="small"
                variant={inMyVendors ? 'outlined' : 'contained'}
                color="primary"
                onClick={openDialog}
                style={{
                    textTransform: 'none',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Button>
            <Dialog
                open={open}
                onClose={() => close()}
                fullWidth
                maxWidth="sm"
                onClick={(event) => event.stopPropagation()}
            >
                <DialogTitle>
                    {inMyVendors
                        ? `Update ${preview?.service_name || ''} pricing`
                        : 'Add to My Vendors'}
                </DialogTitle>
                <DialogContent>
                    {loading || !preview ? (
                        <Typography color="textSecondary">Loading…</Typography>
                    ) : inMyVendors ? (
                        <>
                            <Typography style={{ fontSize: 14 }}>
                                Compare {preview.vendor_name}&apos;s current{' '}
                                {preview.service_name} contract with this bid.
                                Only rows on the bid will be updated.
                            </Typography>
                            <PricingLines lines={preview.lines} showCurrent />
                        </>
                    ) : (
                        <>
                            <Typography style={{ fontSize: 14 }}>
                                {preview.vendor_name} will be added to My
                                Vendors
                                {preview.service_name
                                    ? ` and enabled for ${preview.service_name}`
                                    : ''}
                                . Choose how to handle contract pricing.
                            </Typography>
                            <PricingLines
                                lines={preview.lines}
                                showCurrent={false}
                            />
                            <Box
                                display="flex"
                                flexDirection="column"
                                style={{ gap: 10, marginTop: 16 }}
                            >
                                <ChoiceCard
                                    title="Add and use this bid as the contract"
                                    body={`${preview.vendor_name} gets ${
                                        preview.service_name || 'this service'
                                    }. The bid prices above become their contract.`}
                                    disabled={saving}
                                    onClick={() =>
                                        run(
                                            'add-to-my-vendors',
                                            { apply_pricing: true },
                                            `${bid.vendor.name} added with bid pricing.`,
                                        )
                                    }
                                />
                                <ChoiceCard
                                    title="Add and update pricing later"
                                    body={`${preview.vendor_name} gets ${
                                        preview.service_name || 'this service'
                                    }. Contract prices stay empty until you set them in My Vendors.`}
                                    disabled={saving}
                                    onClick={() =>
                                        run(
                                            'add-to-my-vendors',
                                            { apply_pricing: false },
                                            `${bid.vendor.name} added to My Vendors.`,
                                        )
                                    }
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={close}
                        disabled={saving}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    {inMyVendors ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={saving || loading}
                            onClick={() =>
                                run(
                                    'apply-bid-pricing',
                                    undefined,
                                    'Contract pricing updated.',
                                )
                            }
                            style={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {saving ? 'Saving…' : 'Save pricing'}
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>
        </>
    )
}
